import { forwardRef } from 'react';
import { css, cx } from '@mildang/styled-system/css';
import { splitCssProps } from '@mildang/styled-system/jsx';
import { adornmentButton } from '@mildang/styled-system/recipes';
import type { PandaHTMLStyledProps } from '../../../types/HTMLStyledProps';

interface BaseAdornmentButtonProps {
  /** 아이콘 전용 버튼이라 반드시 지정. Screen reader 접근성. */
  'aria-label': string;
  /** 루트 요소에 추가되는 className. */
  className?: string;
  /** 버튼 안에 렌더할 아이콘. */
  children?: React.ReactNode;
}

export type AdornmentButtonProps = BaseAdornmentButtonProps &
  Omit<PandaHTMLStyledProps<'button'>, 'aria-label' | 'type'>;

/**
 * Input adornment 슬롯에서 사용하는 padding-less 아이콘 버튼.
 *
 * 시각적으로는 20×20 아이콘 그 자체지만 button 시맨틱을 유지한다.
 * `type="button"` 이 강제되므로 폼 안에서 실수로 submit 되지 않는다.
 */
export const AdornmentButton = forwardRef<HTMLButtonElement, AdornmentButtonProps>((props, ref) => {
  const { className, sx, children, ...other } = props;

  const [cssProps, restProps] = splitCssProps(other);
  const { css: _cssStyleProps, ...styleProps } = cssProps;

  return (
    <button
      ref={ref}
      type="button"
      {...restProps}
      className={cx(adornmentButton(), css(styleProps, sx), className)}
    >
      {children}
    </button>
  );
});

AdornmentButton.displayName = 'AdornmentButton';
